/**
 * As operações de manutenção — e as que não têm volta.
 *
 * Cada uma mora na tela de quem já está com o alvo na frente, e não numa tela
 * "Manutenção" genérica: uma tela que pede o id do projeto é justamente onde se
 * digita o id errado.
 *
 * **O que tem volta e o que não tem:**
 *
 * | operação | volta? |
 * |---|---|
 * | apagar todos os pinouts de um projeto | **não** |
 * | apagar todos os pinouts internos de um componente | **não** |
 * | migrar veículo ou motor | **não** — reescreve FK em 21 e 18 tabelas |
 * | limpar o cache do gráfico | sim, regenera sozinho |
 *
 * **Apagar exige dois portões, e é de propósito.** A chave `projeto_excluir` —
 * que o sistema antigo já cobrava e a API tinha deixado cair — e, só no "apagar
 * todos", o nível administrativo no cadastro do operador. Em produção isso são 7
 * pessoas com a chave, 7 no nível, **3 na interseção**. A API deixava qualquer
 * operador logado esvaziar um projeto; agora não deixa. Quem não tem leva 403 com
 * o motivo escrito, em vez de um botão que parece funcionar.
 *
 * **O "apagar todos" confere antes.** `todos/conferir` conta, sem gravar, o que
 * some e o que fica: pinouts, restrições, cache do gráfico invalidado, e o
 * catálogo que fica sem dono — componente, conector, circuito, cor e chicote que
 * nenhum outro pinout usa. O sistema antigo apaga esse catálogo em cascata; esta
 * API mostra a conta e não apaga.
 *
 * **Migrar veículo não funcionava, e o defeito era o nome de três tabelas.** A
 * lista da API dizia `versaos`, `veiculo_categoria` e `veiculo_tipo`; o banco
 * escreve `versao`, `VeiculoCategoria` e `VeiculoTipo`. A migração rodava
 * `UPDATE` em cada uma dentro de uma transação, estourava na primeira e desfazia
 * tudo. **Corrigido** — os nomes certos vieram do SQL do legado, que faz essa
 * migração há anos.
 *
 * A tela continua barrando o aplicar quando a conferência acusa tabela
 * inexistente: a regra é genérica, e é o que vai pegar o próximo nome errado.
 *
 * **A conferência também pergunta ao catálogo do banco duas coisas que nenhuma
 * lista sabia.** Quais linhas colidem com a chave primária do destino — três
 * tabelas carregam o id migrado dentro da PK e não apagam o duplicado antes,
 * então o `UPDATE` viola a chave e desfaz tudo; e quem continua apontando para
 * a origem depois da fusão — o banco não declara chave estrangeira nenhuma para
 * `motor` nem para `veiculo`, e dez tabelas ficam para trás.
 *
 * **A origem nunca é apagada**, apesar do que o resumo das rotas dizia: o
 * cadastro fica, sem vínculo nenhum.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"

// ------------------------------------------------------------- pinouts

/**
 * Apaga **todos** os pinouts de um projeto e devolve quantos foram.
 *
 * `noProjeto` é o que faz a trava barrar isto fora do canário — e aqui ela vale
 * mais do que em qualquer outro lugar do sistema.
 */
export function usarApagarTodosOsPinouts(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const r = await api.delete<{ excluidos: number }>(
        "api/pinouts/todos", { ...noProjeto(projetoId), params: { projetoId } })
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pinouts"] })
      qc.invalidateQueries({ queryKey: ["pinout"] })
    },
  })
}

/**
 * Apaga todos os pinouts internos **de um componente**.
 *
 * **Era por projeto, e não existe por projeto.** A rota recebe `componenteId`; o
 * front mandava `projetoId`, então a chamada caía em componente 0 e não apagava
 * nada — devolvendo `excluidos: 0` com ar de sucesso, enquanto a tela prometia
 * apagar os pinouts internos do projeto inteiro. O sistema antigo também é por
 * componente, e a aba já obriga a escolher um antes de mostrar qualquer coisa.
 *
 * `projetoId` continua vindo, mas só para a trava do canário: o alvo é o
 * componente.
 */
export function usarApagarTodosOsPinoutsInternos(projetoId: number, componenteId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const r = await api.delete<{ excluidos: number }>(
        "api/pinouts-internos/todos", { ...noProjeto(projetoId), params: { componenteId } })
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pinouts-internos"] }),
  })
}

// ------------------------------------------------------------- conferência do apagar

/** Uma tabela de catálogo que fica sem pinout nenhum apontando para ela. */
export type CatalogoOrfao = { tabela: string; linhas: number }

export type ConferenciaDeExclusao = {
  alvo: "projeto" | "componente"
  alvoId: number
  nome: string
  /** `false` = o dono já foi apagado e estas linhas apontam para nada. */
  alvoExiste: boolean
  pinouts: number
  restricoes: number
  cacheDoGrafico: number
  orfaos: CatalogoOrfao[]
  totalOrfao: number
  avisos: string[]
}

/** Um grupo de pinouts cujo projeto (ou componente) não existe mais. */
export type PinoutSemDono = { alvoId: number; pinouts: number }

export type PinoutsSemDono = {
  externos: PinoutSemDono[]
  internos: PinoutSemDono[]
  total: number
}

/**
 * Pinouts que sobreviveram ao dono — **só leitura**.
 *
 * Apagar um projeto não leva os pinouts junto, e apagar um componente não leva os
 * internos. As linhas ficam apontando para um id que não existe, e somem de todas
 * as telas: toda tela do módulo começa escolhendo o alvo numa lista onde ele já
 * não está. Esta consulta é o caminho até elas — **apagar continua sendo a rota
 * de sempre**, com os mesmos dois portões.
 *
 * A rota exige `projeto_excluir`; sem a chave ela recusa, e o bloco simplesmente
 * não aparece.
 */
export function usarPinoutsOrfaos() {
  return useQuery({
    queryKey: ["pinouts-orfaos"] as const,
    staleTime: 60_000,
    retry: false,
    queryFn: () => obter<PinoutsSemDono>("api/pinouts/orfaos"),
  })
}

/**
 * O que "apagar todos" faria, sem fazer. **Não grava nada.**
 *
 * Vale a pena buscar isto e não confiar no total da grade: a grade conta o que o
 * filtro mostra, e o apagar não olha filtro nenhum.
 */
export function usarConferirExclusao(
  escopo: "projeto" | "componente", alvoId: number, ligado: boolean,
) {
  const rota = escopo === "projeto" ? "api/pinouts/todos/conferir" : "api/pinouts-internos/todos/conferir"
  const chave = escopo === "projeto" ? { projetoId: alvoId } : { componenteId: alvoId }

  return useQuery({
    queryKey: ["conferir-exclusao", escopo, alvoId] as const,
    enabled: ligado && alvoId > 0,
    staleTime: 15_000,
    queryFn: () => obter<ConferenciaDeExclusao>(rota, chave),
  })
}

// ------------------------------------------------------------- cache do gráfico

export type LinhaDoCache = {
  componenteId: number
  modeloId: number
  tamanho: number
  arquivo: string | null
  data: string | null
}

/**
 * O que está no cache do gráfico para um componente.
 *
 * O SVG não vem na resposta — só o tamanho, o caminho no servidor legado e a
 * data. É leitura barata, e é o que diz se vale a pena limpar.
 */
export function usarCacheDoGrafico(componenteId: number, modeloId?: number) {
  return useQuery({
    queryKey: ["cache-grafico", componenteId, modeloId] as const,
    enabled: componenteId > 0,
    staleTime: 30_000,
    queryFn: () => obter<LinhaDoCache[] | LinhaDoCache>("api/pinouts/cache-grafico", {
      componenteId, modeloId,
    }),
  })
}

/**
 * Limpa o cache e marca o projeto para reprocessar.
 *
 * **Tem volta**: o desenho se refaz na próxima montagem. Na prática é redundante
 * — toda escrita de pinout já marca `projeto.atualizar` e o legado ignora o cache
 * nesse estado —, e existe para forçar a regeneração pontualmente.
 */
export function usarLimparCacheDoGrafico(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ componenteId, modeloId }: { componenteId: number; modeloId?: number }) => {
      const r = await api.delete<{ removidos: number }>("api/pinouts/cache-grafico", {
        ...noProjeto(projetoId), params: { componenteId, modeloId },
      })
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cache-grafico"] }),
  })
}

// ------------------------------------------------------------- migração

export type TabelaDaMigracao = {
  tabela: string
  /** `-1` = a tabela **não existe** no banco, e a migração vai estourar nela. */
  linhas: number
}

export type LinhaApagada = { tabela: string; linhas: number; porque: string }

/**
 * Linhas da origem que já têm a equivalente no destino numa tabela **sem passo de dedup**.
 *
 * `dado_motor`, `dado_veiculo` e `peca_veiculo_motor` carregam o id migrado dentro da chave
 * primária. O `UPDATE` viola a chave, a transação estoura e desfaz tudo — o mesmo defeito que
 * `TabelaPesquisa` resolve apagando o duplicado antes, e que essas três não resolvem.
 * `chave` diz em que colunas as duas linhas se encontram.
 */
export type LinhaQueColide = { tabela: string; linhas: number; chave: string }

/**
 * Linhas que apontam para a origem numa tabela que a migração **não toca**.
 *
 * Não impedem nada — é conteúdo que fica partido entre os dois cadastros. O banco não declara
 * chave estrangeira para `motor` nem para `veiculo`, então a lista da migração é convenção:
 * quem responde quem mais aponta é o catálogo.
 */
export type LinhaQueFica = { tabela: string; linhas: number }

export type ConferenciaDaMigracao = {
  tipo: string
  origemId: number
  origem: string
  destinoId: number
  destino: string
  totalMovido: number
  totalRemovido: number
  movidas: TabelaDaMigracao[]
  removidas: LinhaApagada[]
  colisoes: LinhaQueColide[]
  ficam: LinhaQueFica[]
  avisos: string[]
}

export type TipoDeMigracao = "veiculo" | "motor"

/** O que a migração faria, sem fazer. Nada é gravado. */
export function usarConferirMigracao(
  tipo: TipoDeMigracao, origemId: number, destinoId: number, ligado: boolean,
) {
  return useQuery({
    queryKey: ["migracao-conferir", tipo, origemId, destinoId] as const,
    enabled: ligado && origemId > 0 && destinoId > 0 && origemId !== destinoId,
    staleTime: 30_000,
    queryFn: () => obter<ConferenciaDaMigracao>(
      `api/migracoes/${tipo}/conferir`, { origemId, destinoId }),
  })
}

/** Aplica. **Não tem volta.** */
export function usarMigrar(tipo: TipoDeMigracao) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ origemId, destinoId }: { origemId: number; destinoId: number }) => {
      const r = await api.post<{ message: string }>(
        `api/migracoes/${tipo}`, { origemId, destinoId },
        // Dado global: a trava recusa em desenvolvimento, e faz bem.
        { headers: { "X-Sem-Projeto": "1" } })
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["migracao-conferir"] })
      qc.invalidateQueries({ queryKey: ["catalogo-conteudo"] })
    },
  })
}

/** A tabela que não existe é o que impede a migração de completar. */
export function tabelasQueFaltam(c: ConferenciaDaMigracao | undefined): string[] {
  return (c?.movidas ?? []).filter((m) => m.linhas < 0).map((m) => m.tabela)
}

/**
 * Tudo o que faz a transação voltar atrás — em frase, pronto para a tela.
 *
 * São dois motivos e os dois terminam igual: o `UPDATE` estoura no meio e nada acontece. Vazio
 * quer dizer que a conferência não achou impedimento, **não** que a migração é reversível.
 */
export function impedimentos(c: ConferenciaDaMigracao | undefined): string[] {
  const faltam = tabelasQueFaltam(c)
  return [
    ...(faltam.length > 0 ? [`${faltam.join(", ")} não existe(m) no banco`] : []),
    ...(c?.colisoes ?? []).map(
      (x) => `${x.tabela}: ${x.linhas} linha(s) já existem no destino com a mesma chave (${x.chave})`),
  ]
}
